// @flow
import React, {useState, useEffect, useCallback, useRef} from 'react';
import makeStyles from "@material-ui/core/styles/makeStyles";
import Box from "@material-ui/core/Box";
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import InfiniteScroll from 'react-infinite-scroller';
import type { NewMessage as NewMessageType } from '../../types';
import Message from '../../models/Message';
import Spinner from '../ui/Spinner';
import { useSidebarPaperRef } from '../Sidebar/context';
import NewMessage from './NewMessage';
import MessageList from './MessageList';


const useStyles = makeStyles(theme => ({
    root: {
        position: 'relative'
    },
    button: {
        position: 'absolute',
        top: 0,
        right: 0,
        zIndex: 1,
    }
}));

export type Props = $ReadOnly<{
    icao?: string,
    userId?: string,
    showAll?: boolean,
    disableForm?: boolean,

    sendMessage: (message: NewMessageType) => void,
    onClose: () => void
}>;

function Messages({ icao, userId, showAll, disableForm, sendMessage, onClose }: Props) {
    const classes = useStyles();
    const [fetched, update] = useState();
    const [messages, setMessages] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const sidebarPaperRef = useSidebarPaperRef();
    const nextRef = useRef();

    if(showAll && (icao || userId)) {
        throw new Error('ShowAll and icao/userId was set at the same time');
    }

    const onLoad = useCallback((data, { last }) => {
        messages.push(...data);

        messages.sort((a, b) => {
            if (a.date > b.date) {
                return -1;
            } else if (a.date < b.date) {
                return 1;
            } else {
                return 0;
            }
        });

        update({});

        if(last) {
            setHasMore(false);
        }
    }, [messages]);

    const onAdd = useCallback((message) => {
        messages.push(message);

        messages.sort((a, b) => {
            if (a.date > b.date) {
                return -1;
            } else if (a.date < b.date) {
                return 1;
            } else {
                return 0;
            }
        });

        update({});
    }, [messages]);

    const onChange = useCallback((newValue) => {
        const index = messages.findIndex(message => message.id === newValue.id);

        if (index !== -1) {
            messages.splice(index, 1, newValue);
        } else {
            messages.push(newValue);
        }

        update({});
    }, [messages]);

    const onSubmit = (message) => {
        sendMessage({
            icao: icao,
            userId: userId,
            body: message
        });
    };

    useEffect(() => {
        const query = showAll ? undefined : [['icao', '==', icao ? icao : null]];
        const { unsubscribe, next } = Message.subscribe({
            query, onLoad, onAdd, onChange, onSubscribe: () => update({}),
            paging: {
                itemsPerPage: 20,
                orderBy: 'date'
            }
        });

        nextRef.current = next;

        return () => {
            setMessages([]);
            unsubscribe();
        };
    }, [icao, onLoad, userId, showAll, onAdd, onChange]);

    const loadMore = useCallback(() => {
        if(nextRef.current) {
            nextRef.current();
        }
    }, []);

    const getScrollParent = useCallback(() => sidebarPaperRef.current, [sidebarPaperRef]);

    return (
        <div>
            {!disableForm &&
                <Box mx={2} mt={2} className={classes.root}>
                    {onClose && <IconButton onClick={onClose} className={classes.button}>
                        <CloseIcon/>
                    </IconButton>}
                    <NewMessage multiple={Boolean(!icao)} onSubmit={onSubmit}/>
                </Box>
            }
            <Spinner isLoading={!fetched}>
                {fetched && messages &&
                    <InfiniteScroll
                        loadMore={loadMore}
                        hasMore={hasMore}
                        initialLoad={true}
                        useWindow={false}
                        getScrollParent={getScrollParent}
                    >
                        <MessageList messages={messages} showRecipient={showAll} hideDivider={disableForm} />
                    </InfiniteScroll>
                }
            </Spinner>
        </div>
    );
}

export default Messages;